import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';
import type { AdAccountConfig, AdSyncLogEntry, AdPlatform, CachedLeadForm, LeadFormConfig, CampaignFormEntry, MarketingReportSettings } from '../types/marketing';

/** Esito di "Aggiorna pagine" (edge meta-ads-refresh-pages). */
export type MetaPagesRefreshResult =
  | {
      ok: true;
      added: Array<{ id: string; name: string }>;
      excluded: Array<{ id: string; name: string }>;
      /** Pagine usate dagli annunci dell'org ma non accessibili col token attuale. */
      inaccessibleAdPages: Array<{ id: string; name: string | null }>;
    }
  | { ok: false; error: string };

/** Passi di "Aggiorna da Meta": pagine, campagne e moduli, statistiche, ricezione lead. */
export const META_UPDATE_STEPS = ['Pagine', 'Campagne e moduli', 'Statistiche', 'Ricezione lead'] as const;
export interface MetaUpdateState {
  /** Indice del passo in corso (0-3); 4 = finito. */
  step: number;
  running: boolean;
  error: string | null;
  finishedAt: string | null;
  summary: { campaigns: number; forms: number; newPages: number } | null;
}

interface AdAccountStore {
  metaConfig: AdAccountConfig | null;
  googleConfig: AdAccountConfig | null;
  linkedinConfig: AdAccountConfig | null;
  loading: boolean;
  error: string | null;
  syncLog: AdSyncLogEntry[];
  syncLogLoading: boolean;
  leadForms: CachedLeadForm[] | null;
  leadFormsLoading: boolean;
  campaignFormMap: CampaignFormEntry[];
  campaignFormMapLoading: boolean;
  syncProgress: { step: number; totalSteps: number; label: string } | null;
  metaUpdate: MetaUpdateState | null;
  /** Un solo comando per leggere tutto da Meta, a passi, senza bloccare la schermata. */
  runMetaUpdate: () => Promise<boolean>;

  fetchConfigs: () => Promise<void>;
  initiateMetaAuth: () => Promise<string | null>;
  disconnectAccount: (platform: AdPlatform) => Promise<boolean>;
  updateAccountConfig: (platform: AdPlatform, updates: Partial<AdAccountConfig>) => Promise<boolean>;
  patchMetaSettings: (patch: Record<string, any>) => Promise<boolean>;
  updateLeadFormMapping: (mapping: Record<string, string>) => Promise<boolean>;
  updateDefaultLeadSettings: (settings: {
    default_lead_source_id?: string;
    default_pipeline_id?: string;
    default_pipeline_stage_id?: string;
    default_stato_cliente_id?: string;
    default_assigned_to_id?: string | null;
    default_assigned_to_pool?: string[];
    assignment_mode?: 'none' | 'specific' | 'pool' | 'auto';
    default_interest_category_ids?: string[];
    default_tag_ids?: string[];
    dedup_by_phone?: boolean;
    lead_sync_enabled?: boolean;
    field_concat_config?: Record<string, string[]>;
  }) => Promise<boolean>;
  updateGoogleLeadSettings: (settings: {
    default_lead_source_id?: string | null;
    default_pipeline_id?: string | null;
    default_pipeline_stage_id?: string | null;
    default_stato_cliente_id?: string | null;
    default_assigned_to_id?: string | null;
    default_assigned_to_pool?: string[] | null;
    assignment_mode?: 'none' | 'specific' | 'pool' | 'auto';
    default_interest_category_ids?: string[] | null;
    default_tag_ids?: string[] | null;
    dedup_mode?: 'update' | 'create_new' | 'skip';
    dedup_by_phone?: boolean;
    lead_sync_enabled?: boolean;
  }) => Promise<boolean>;
  generateGoogleWebhookKey: () => Promise<string | null>;
  // LinkedIn Ads: routing lead (stessa forma di updateGoogleLeadSettings) + connessione OAuth.
  updateLinkedInLeadSettings: (settings: {
    default_lead_source_id?: string | null;
    default_pipeline_id?: string | null;
    default_pipeline_stage_id?: string | null;
    default_stato_cliente_id?: string | null;
    default_assigned_to_id?: string | null;
    default_assigned_to_pool?: string[] | null;
    assignment_mode?: 'none' | 'specific' | 'pool' | 'auto';
    default_interest_category_ids?: string[] | null;
    default_tag_ids?: string[] | null;
    dedup_mode?: 'update' | 'create_new' | 'skip';
    dedup_by_phone?: boolean;
    lead_sync_enabled?: boolean;
  }) => Promise<boolean>;
  connectLinkedIn: () => Promise<void>;
  selectMetaAdAccount: (accountId: string, accountName: string) => Promise<boolean>;
  selectMetaPage: (pageId: string) => Promise<boolean>;
  updateVisibleAdAccounts: (accountIds: string[]) => Promise<boolean>;
  updateVisiblePages: (pageIds: string[]) => Promise<boolean>;
  refreshMetaPages: () => Promise<MetaPagesRefreshResult>;
  selectGoogleCustomer: (customerId: string, customerName: string) => Promise<boolean>;
  triggerManualSync: (platform: AdPlatform) => Promise<boolean>;
  triggerBulkAttribution: (mode?: 'utm_match' | 'leadgen_retroactive' | 'all') => Promise<boolean>;
  triggerManualAttribution: (clientIds: string[], campaignId: string) => Promise<{ success: boolean; assigned_count?: number; campaign_name?: string }>;
  fetchLeadPreviews: (sinceDate: string) => Promise<{ success: boolean; leads: any[]; forms_checked?: number }>;
  /** `outcomes` mappa ogni leadgen_id al suo esito reale (created, updated, skipped_form_disabled, ...): serve alla UI per dire PERCHE' un lead non e' entrato. */
  importSelectedLeads: (leads: Array<{ leadgen_id: string; form_id: string; page_id?: string }>) => Promise<{ success: boolean; leads_processed?: number; errors?: number; outcomes?: Record<string, string> }>;
  fetchSyncLog: (platform?: string) => Promise<void>;
  fetchLeadForms: () => Promise<void>;
  testFormMapping: (formId: string) => Promise<any>;
  testSaveLead: (formId: string, campaignId: string | undefined, leadData: any) => Promise<any>;
  fetchTestLeads: (formId: string, count?: number) => Promise<any[]>;
  updateFormConfig: (formId: string, config: Partial<LeadFormConfig>) => Promise<boolean>;
  removeFormConfig: (formId: string) => Promise<boolean>;
  syncSingleForm: (formId: string) => Promise<{ ok: boolean; error?: string; hint?: string; code?: string }>;
  fetchCampaignFormMap: () => Promise<void>;
  updateCampaignFormConfig: (campaignExtId: string, formId: string, config: Partial<LeadFormConfig>) => Promise<boolean>;
  removeCampaignFormConfig: (campaignExtId: string, formId: string) => Promise<boolean>;
  getMarketingReportSettings: () => MarketingReportSettings | null;
  updateMarketingReportSettings: (settings: MarketingReportSettings) => Promise<boolean>;
  diagnoseCampaigns: () => Promise<{ total_meta: number; total_crm: number; missing_in_crm: Array<{ id: string; name: string; status: string; objective?: string }>; missing_in_crm_count: number; in_crm_not_meta: Array<{ id: string; name: string; status: string }>; in_crm_not_meta_count: number } | null>;
}

export const useAdAccountStore = create<AdAccountStore>((set, get) => ({
  metaConfig: null,
  googleConfig: null,
  linkedinConfig: null,
  loading: false,
  error: null,
  syncLog: [],
  syncLogLoading: false,
  leadForms: null,
  leadFormsLoading: false,
  campaignFormMap: [],
  campaignFormMapLoading: false,
  syncProgress: null,
  metaUpdate: null,

  fetchConfigs: async () => {
    set({ loading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase
        .from('ad_account_configs')
        .select('*')
        .eq('organization_id', organizationId);

      if (error) throw error;

      const metaConfig = data?.find(c => c.platform === 'meta') || null;
      const googleConfig = data?.find(c => c.platform === 'google') || null;
      const linkedinConfig = data?.find(c => c.platform === 'linkedin') || null;

      set({ metaConfig, googleConfig, linkedinConfig, loading: false });
    } catch (error: any) {
      console.error('Error fetching ad account configs:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  initiateMetaAuth: async () => {
    set({ error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase.functions.invoke('meta-ads-auth', {
        body: { organization_id: organizationId },
      });

      if (error) throw error;
      if (!data?.authorization_url) throw new Error('No authorization URL returned');

      return data.authorization_url;
    } catch (error: any) {
      console.error('Error initiating Meta Ads auth:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  disconnectAccount: async (platform: AdPlatform) => {
    set({ error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { error } = await supabase
        .from('ad_account_configs')
        .update({
          is_connected: false,
          access_token: null,
          refresh_token: null,
          token_expires_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq('organization_id', organizationId)
        .eq('platform', platform);

      if (error) throw error;

      if (platform === 'meta') {
        set({ metaConfig: get().metaConfig ? { ...get().metaConfig!, is_connected: false } : null });
      } else if (platform === 'linkedin') {
        set({ linkedinConfig: get().linkedinConfig ? { ...get().linkedinConfig!, is_connected: false } : null });
      } else {
        set({ googleConfig: get().googleConfig ? { ...get().googleConfig!, is_connected: false } : null });
      }
      return true;
    } catch (error: any) {
      console.error(`Error disconnecting ${platform} account:`, error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  updateAccountConfig: async (platform: AdPlatform, updates: Partial<AdAccountConfig>) => {
    set({ error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const updatedAt = new Date().toISOString();
      const { error } = await supabase
        .from('ad_account_configs')
        .update({ ...updates, updated_at: updatedAt })
        .eq('organization_id', organizationId)
        .eq('platform', platform);

      if (error) throw error;

      // Update ottimistico: aggiorna lo store locale senza refetch (evita loading: true)
      const currentConfig = platform === 'meta'
        ? get().metaConfig
        : platform === 'linkedin'
          ? get().linkedinConfig
          : get().googleConfig;
      if (currentConfig) {
        const updatedConfig = { ...currentConfig, ...updates, updated_at: updatedAt };
        // Deep merge settings se presente
        if ((updates as any).settings && currentConfig.settings) {
          updatedConfig.settings = { ...currentConfig.settings, ...(updates as any).settings };
        }
        if (platform === 'meta') {
          set({ metaConfig: updatedConfig });
        } else if (platform === 'linkedin') {
          set({ linkedinConfig: updatedConfig });
        } else {
          set({ googleConfig: updatedConfig });
        }
      }
      return true;
    } catch (error: any) {
      console.error(`Error updating ${platform} config:`, error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  // Aggiornamento MIRATO e atomico delle sole chiavi passate in `patch`, via RPC
  // `update_ad_config_settings` (settings = settings || patch, server-side). Evita il
  // read-modify-write dell'intero blob JSONB da snapshot client stale, che poteva
  // cancellare mappature/pipeline sovrascrivendo modifiche concorrenti (es. le edge di
  // sync che riscrivono cached_lead_forms/cached_campaign_form_map).
  // Fallback al metodo legacy full-blob se la RPC non è ancora deployata.
  patchMetaSettings: async (patch: Record<string, any>) => {
    const config = get().metaConfig;
    if (!config) return false;
    set({ error: null });
    try {
      const { error } = await supabase.rpc('update_ad_config_settings', {
        p_config_id: config.id,
        p_patch: patch,
      });
      if (error) throw error;
      // Update locale shallow (coerente col merge server-side)
      set({
        metaConfig: {
          ...config,
          settings: { ...(config.settings || {}), ...patch },
          updated_at: new Date().toISOString(),
        },
      });
      return true;
    } catch (error: any) {
      // Il fallback full-blob vale SOLO quando la RPC non esiste ancora (PGRST202 lato
      // PostgREST, 42883 lato Postgres). Ogni altro errore è un rifiuto legittimo — chiave
      // protetta, org sbagliata, pagine non autorizzate dal trigger — e ritentarlo sul
      // percorso full-blob lo fa rifiutare di nuovo mostrando una causa sbagliata.
      const rpcMissing = error?.code === 'PGRST202' || error?.code === '42883';
      if (!rpcMissing) {
        console.error('patchMetaSettings rifiutata:', error?.code, error?.message);
        set({ error: friendlyDbError(error) });
        return false;
      }
      console.warn('patchMetaSettings RPC non disponibile, fallback full-blob:', error?.message);
      const newSettings = { ...(config.settings || {}), ...patch };
      return get().updateAccountConfig('meta', { settings: newSettings } as any);
    }
  },

  updateLeadFormMapping: async (mapping: Record<string, string>) => {
    if (!get().metaConfig) return false;
    return get().patchMetaSettings({ lead_form_field_mapping: mapping });
  },

  updateDefaultLeadSettings: async (settings) => {
    if (!get().metaConfig) return false;
    return get().patchMetaSettings({ ...settings });
  },

  updateGoogleLeadSettings: async (settings) => {
    const config = get().googleConfig;
    if (!config) return false;
    const newSettings = { ...config.settings, ...settings };
    return get().updateAccountConfig('google', { settings: newSettings } as any);
  },

  generateGoogleWebhookKey: async () => {
    const config = get().googleConfig;
    if (!config) return null;
    // Chiave forte (64 hex) da incollare nel Lead Form Google Ads come "Key".
    const key = (crypto.randomUUID() + crypto.randomUUID()).replace(/-/g, '');
    const newSettings = { ...config.settings, google_webhook_key: key };
    const ok = await get().updateAccountConfig('google', { settings: newSettings } as any);
    return ok ? key : null;
  },

  updateLinkedInLeadSettings: async (settings) => {
    const config = get().linkedinConfig;
    if (!config) return false;
    const newSettings = { ...config.settings, ...settings };
    return get().updateAccountConfig('linkedin', { settings: newSettings } as any);
  },

  connectLinkedIn: async () => {
    // OAuth su gateway: passa il JWT Supabase, il gateway risolve l'org e reindirizza a LinkedIn.
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      set({ error: 'Sessione non valida' });
      return;
    }
    window.location.href = `https://api.flusia.it/oauth/linkedin/authorize?token=${encodeURIComponent(token)}`;
  },

  selectMetaAdAccount: async (accountId: string, accountName: string) => {
    return get().updateAccountConfig('meta', {
      account_id: accountId,
      account_name: accountName,
    });
  },

  selectMetaPage: async (pageId: string) => {
    return get().updateAccountConfig('meta', { meta_page_id: pageId } as any);
  },

  updateVisibleAdAccounts: async (accountIds: string[]) => {
    if (!get().metaConfig) return false;
    return get().patchMetaSettings({ visible_ad_account_ids: accountIds });
  },

  // Binding esplicito pagine Facebook ↔ organizzazione (account agenzia multi-pagina):
  // limita form, moduli orfani e routing webhook alle sole pagine selezionate.
  // Lista vuota/assente = comportamento legacy (tutte le pagine disponibili).
  updateVisiblePages: async (pageIds: string[]) => {
    if (!get().metaConfig) return false;
    return get().patchMetaSettings({ visible_page_ids: pageIds });
  },

  // "Aggiorna pagine": rilegge da Meta le pagine accessibili col token salvato e aggiunge
  // le nuove, senza scollegare. Poi l'health-check sottoscrive il webhook lead sulle nuove.
  refreshMetaPages: async () => {
    try {
      const { data, error } = await supabase.functions.invoke('meta-ads-refresh-pages', { body: {} });
      if (error) throw error;
      if (!data?.success) {
        return { ok: false, error: data?.error || 'Aggiornamento delle pagine non riuscito.' };
      }
      if ((data.added || []).length > 0) {
        const organizationId = await getCurrentUserOrganizationId();
        await supabase.functions.invoke('meta-ads-health-check', {
          body: { organization_id: organizationId },
        }).catch(() => {});
      }
      await get().fetchConfigs();
      return {
        ok: true,
        added: data.added || [],
        excluded: data.excluded || [],
        inaccessibleAdPages: data.inaccessible_ad_pages || [],
      };
    } catch (error: any) {
      console.error('Error refreshing Meta pages:', error);
      return { ok: false, error: friendlyDbError(error) || 'Errore di rete durante l\'aggiornamento delle pagine.' };
    }
  },

  selectGoogleCustomer: async (customerId: string, customerName: string) => {
    set({ error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('ad_account_configs')
        .upsert({
          organization_id: organizationId,
          platform: 'google',
          account_id: customerId,
          account_name: customerName,
          google_user_id: user.id,
          is_connected: true,
          last_connected_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }, { onConflict: 'organization_id,platform' });

      if (error) throw error;
      await get().fetchConfigs();
      return true;
    } catch (error: any) {
      console.error('Error selecting Google customer:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  triggerManualSync: async (platform: AdPlatform) => {
    set({ error: null, syncProgress: { step: 1, totalSteps: 3, label: 'Sincronizzazione campagne...' } });
    try {
      const organizationId = await getCurrentUserOrganizationId();

      // Fire-and-forget: la edge function può impiegare >60s e il relay va in timeout.
      // Lanciamo la chiamata senza attendere la response, poi polliamo ad_sync_log.
      supabase.functions.invoke('ad-sync-campaigns', {
        body: { organization_id: organizationId, platform },
      }).catch(() => {}); // ignora timeout/errori HTTP — controlliamo il log

      // Poll ad_sync_log ogni 5s per max 5 minuti
      const maxWaitMs = 5 * 60 * 1000;
      const pollIntervalMs = 5000;
      const startTime = Date.now();
      let syncCompleted = false;
      let syncFailed = false;
      let syncError = '';
      let entitiesSynced = 0;

      while (Date.now() - startTime < maxWaitMs) {
        await new Promise(r => setTimeout(r, pollIntervalMs));
        const elapsed = Math.round((Date.now() - startTime) / 1000);
        set({ syncProgress: { step: 1, totalSteps: 3, label: `Sincronizzazione campagne... (${elapsed}s)` } });

        const { data: logs } = await supabase
          .from('ad_sync_log')
          .select('status, entities_synced, error_message')
          .eq('organization_id', organizationId)
          .eq('platform', platform)
          .eq('sync_type', 'campaigns')
          .order('started_at', { ascending: false })
          .limit(1);

        const latest = logs?.[0];
        if (latest?.status === 'success') {
          entitiesSynced = latest.entities_synced || 0;
          syncCompleted = true;
          break;
        }
        if (latest?.status === 'failed') {
          syncFailed = true;
          syncError = latest.error_message || 'Errore sconosciuto';
          break;
        }
        // status 'started' → continua a pollare
      }

      if (syncFailed) {
        throw new Error(syncError);
      }

      if (!syncCompleted) {
        // Timeout polling — sync probabilmente ancora in corso in background
        set({ syncProgress: { step: 1, totalSteps: 3, label: 'Sync in corso in background, ricarica la pagina tra poco' } });
        await new Promise(r => setTimeout(r, 3000));
        await get().fetchConfigs();
        return true;
      }

      set({ syncProgress: { step: 1, totalSteps: 3, label: `Campagne sincronizzate (${entitiesSynced})` } });

      // Step 2: sync stats (tipicamente veloce)
      set({ syncProgress: { step: 2, totalSteps: 3, label: 'Sincronizzazione statistiche...' } });
      const { error: statsError } = await supabase.functions.invoke('ad-sync-stats', {
        body: { organization_id: organizationId, platform },
      });
      if (statsError) throw statsError;

      // Step 3: health check (fire-and-forget)
      if (platform === 'meta') {
        set({ syncProgress: { step: 3, totalSteps: 3, label: 'Verifica connessione...' } });
        await supabase.functions.invoke('meta-ads-health-check', {
          body: { organization_id: organizationId },
        }).catch(() => {});
      }

      await get().fetchConfigs();
      return true;
    } catch (error: any) {
      console.error(`Error triggering ${platform} sync:`, error);
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ syncProgress: null });
    }
  },

  runMetaUpdate: async () => {
    if (get().metaUpdate?.running) return false;
    const setStep = (step: number) => set({ metaUpdate: { step, running: true, error: null, finishedAt: null, summary: null } });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      // 1. Pagine nuove (stessa funzione di "Aggiorna pagine")
      setStep(0);
      const pages = await get().refreshMetaPages();
      const newPages = pages.ok ? pages.added.length : 0;
      // 2. Campagne e moduli: la funzione puo' superare il timeout del relay, quindi si legge il registro.
      setStep(1);
      const startedAt = new Date(Date.now() - 2000).toISOString();
      supabase.functions.invoke('ad-sync-campaigns', { body: { organization_id: organizationId, platform: 'meta' } }).catch(() => {});
      let campaignsDone = false;
      for (let waited = 0; waited < 6 * 60 * 1000 && !campaignsDone; waited += 4000) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await supabase
          .from('ad_sync_log')
          .select('status, error_message')
          .eq('organization_id', organizationId)
          .eq('platform', 'meta')
          .eq('sync_type', 'campaigns')
          .gte('started_at', startedAt)
          .order('started_at', { ascending: false })
          .limit(1);
        const latest = data?.[0];
        if (latest?.status === 'failed') throw new Error(latest.error_message || 'Aggiornamento campagne non riuscito');
        if (latest?.status === 'success') campaignsDone = true;
      }
      // 3. Statistiche (gli account oltre al principale proseguono da soli in esecuzioni separate)
      setStep(2);
      await supabase.functions.invoke('ad-sync-stats', { body: { organization_id: organizationId, platform: 'meta' } }).catch(() => {});
      // 4. Ricezione lead: ri-sottoscrive le pagine e verifica i token
      setStep(3);
      await supabase.functions.invoke('meta-ads-health-check', { body: { organization_id: organizationId } }).catch(() => {});
      await get().fetchConfigs();
      await get().fetchCampaignFormMap();
      const settings: any = get().metaConfig?.settings || {};
      const summary = {
        campaigns: Object.keys(settings.cached_campaign_form_map || {}).length,
        forms: Array.isArray(settings.cached_lead_forms) ? settings.cached_lead_forms.length : 0,
        newPages,
      };
      set({ metaUpdate: { step: 4, running: false, error: null, finishedAt: new Date().toISOString(), summary } });
      return true;
    } catch (error: any) {
      console.error('[runMetaUpdate]', error);
      set((st) => ({ metaUpdate: { step: st.metaUpdate?.step ?? 0, running: false, error: friendlyDbError(error), finishedAt: null, summary: null } }));
      await get().fetchConfigs();
      return false;
    }
  },

  triggerBulkAttribution: async (mode = 'all') => {
    set({ error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { error } = await supabase.functions.invoke('ad-attribution-bulk', {
        body: { organization_id: organizationId, mode },
      });
      if (error) throw error;
      return true;
    } catch (error: any) {
      console.error('Error triggering bulk attribution:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  triggerManualAttribution: async (clientIds: string[], campaignId: string) => {
    set({ error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase.functions.invoke('ad-attribution-manual', {
        body: { organization_id: organizationId, client_ids: clientIds, campaign_id: campaignId },
      });
      if (error) throw error;
      return data || { success: false };
    } catch (error: any) {
      console.error('Error triggering manual attribution:', error);
      set({ error: friendlyDbError(error) });
      return { success: false };
    }
  },


  fetchLeadPreviews: async (sinceDate: string) => {
    set({ error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase.functions.invoke('meta-leadads-recovery', {
        body: { organization_id: organizationId, since_date: sinceDate, action: 'preview' },
      });
      if (error) throw error;
      return data || { success: false, leads: [] };
    } catch (error: any) {
      console.error('Error fetching lead previews:', error);
      set({ error: friendlyDbError(error) });
      return { success: false, leads: [] };
    }
  },

  importSelectedLeads: async (leads: Array<{ leadgen_id: string; form_id: string; page_id?: string }>) => {
    set({ error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase.functions.invoke('meta-leadads-recovery', {
        body: { organization_id: organizationId, action: 'import', lead_ids: leads },
      });
      if (error) throw error;
      return data || { success: false };
    } catch (error: any) {
      console.error('Error importing selected leads:', error);
      set({ error: friendlyDbError(error) });
      return { success: false };
    }
  },

  fetchSyncLog: async (platform?: string) => {
    set({ syncLogLoading: true });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      let query = supabase
        .from('ad_sync_log')
        .select('*')
        .eq('organization_id', organizationId)
        .order('started_at', { ascending: false })
        .limit(50);

      if (platform) {
        query = query.eq('platform', platform);
      }

      const { data, error } = await query;
      if (error) throw error;

      set({ syncLog: data || [], syncLogLoading: false });
    } catch (error: any) {
      console.error('Error fetching sync log:', error);
      set({ syncLogLoading: false });
    }
  },

  fetchLeadForms: async () => {
    set({ leadFormsLoading: true });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase.functions.invoke('meta-leadads-forms', {
        body: { organization_id: organizationId },
      });
      if (error) throw error;
      set({ leadForms: data?.forms || [], leadFormsLoading: false });
      await get().fetchConfigs();
    } catch (error: any) {
      console.error('Error fetching lead forms:', error);
      set({ leadFormsLoading: false, error: friendlyDbError(error) });
    }
  },

  testFormMapping: async (formId: string) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase.functions.invoke('meta-leadads-forms', {
        body: { organization_id: organizationId, test_form_id: formId, test_lead_count: 1 },
      });
      if (error) throw error;
      return data?.test_lead || null;
    } catch (error: any) {
      console.error('Error testing form mapping:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  testSaveLead: async (formId: string, campaignId: string | undefined, leadData: any) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase.functions.invoke('meta-leadads-webhook', {
        body: {
          test_mode: true,
          organization_id: organizationId,
          form_id: formId,
          campaign_id: campaignId,
          lead_data: leadData,
        },
      });
      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error test-saving lead:', error);
      throw error;
    }
  },

  autoMapFields: async (metaFields: Array<{ key: string; label: string }>, crmFields: Array<{ value: string; label: string }>, exampleValues?: Record<string, string>) => {
    try {
      const { data, error } = await supabase.functions.invoke('meta-leadads-automap', {
        body: { meta_fields: metaFields, crm_fields: crmFields, example_values: exampleValues || {} },
      });
      if (error) throw error;
      return data as { mapping: Record<string, string>; concat: Record<string, string[]> | null; tokens_used: number };
    } catch (error: any) {
      console.error('Error auto-mapping fields:', error);
      throw error;
    }
  },

  fetchTestLeads: async (formId: string, count = 3) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase.functions.invoke('meta-leadads-forms', {
        body: { organization_id: organizationId, test_form_id: formId, test_lead_count: count },
      });
      if (error) throw error;
      return data?.test_leads || (data?.test_lead ? [data.test_lead] : []);
    } catch (error: any) {
      console.error('Error fetching test leads:', error);
      return [];
    }
  },

  updateFormConfig: async (formId: string, config: Partial<LeadFormConfig>) => {
    const metaConfig = get().metaConfig;
    if (!metaConfig) return false;

    const currentFormConfigs = metaConfig.settings?.form_configs || {};
    const updatedFormConfigs = {
      ...currentFormConfigs,
      [formId]: { ...currentFormConfigs[formId], ...config },
    };
    return get().patchMetaSettings({ form_configs: updatedFormConfigs });
  },

  removeFormConfig: async (formId: string) => {
    const metaConfig = get().metaConfig;
    if (!metaConfig) return false;

    const currentFormConfigs = { ...(metaConfig.settings?.form_configs || {}) };
    delete currentFormConfigs[formId];
    return get().patchMetaSettings({ form_configs: currentFormConfigs });
  },

  syncSingleForm: async (formId: string) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase.functions.invoke('meta-leadads-forms', {
        body: { organization_id: organizationId, sync_form_id: formId },
      });
      if (error) throw error;
      if (!data?.success) {
        // La edge function risponde 200 + success:false con error/hint diagnostici
        return {
          ok: false,
          error: data?.error || 'Sincronizzazione non riuscita.',
          hint: data?.hint,
          code: data?.code,
        };
      }
      await get().fetchConfigs();
      return { ok: true };
    } catch (error: any) {
      console.error('Error syncing single form:', error);
      return { ok: false, error: friendlyDbError(error) || 'Errore di rete durante la sincronizzazione.' };
    }
  },

  fetchCampaignFormMap: async () => {
    set({ campaignFormMapLoading: true });
    try {
      const metaConfig = get().metaConfig;
      if (!metaConfig) {
        set({ campaignFormMap: [], campaignFormMapLoading: false });
        return;
      }

      // Legge la mappa campagna→form dal cache (costruita da ad-sync-campaigns)
      const cachedMap: Record<string, { campaign_name: string; campaign_status: string; forms: Array<{ form_id: string; form_name: string }> }> =
        metaConfig.settings?.cached_campaign_form_map || {};

      const result: CampaignFormEntry[] = Object.entries(cachedMap).map(([extId, data]) => ({
        campaign_external_id: extId,
        campaign_name: data.campaign_name || 'Campagna sconosciuta',
        campaign_status: data.campaign_status || 'UNKNOWN',
        forms: data.forms || [],
      }));

      // Ordina: ACTIVE prima, poi per nome
      result.sort((a, b) => {
        if (a.campaign_status === 'ACTIVE' && b.campaign_status !== 'ACTIVE') return -1;
        if (a.campaign_status !== 'ACTIVE' && b.campaign_status === 'ACTIVE') return 1;
        return a.campaign_name.localeCompare(b.campaign_name);
      });

      console.log(`[CampaignFormMap] Found ${result.length} campaigns from cache`);
      set({ campaignFormMap: result, campaignFormMapLoading: false });
    } catch (error: any) {
      console.error('Error fetching campaign form map:', error);
      set({ campaignFormMapLoading: false });
    }
  },

  updateCampaignFormConfig: async (campaignExtId: string, formId: string, config: Partial<LeadFormConfig>) => {
    const metaConfig = get().metaConfig;
    if (!metaConfig) return false;

    // Merge server-side sul SOLO form indicato: due sessioni aperte in parallelo
    // non si sovrascrivono più l'intero campaign_form_configs a vicenda.
    try {
      const { error } = await supabase.rpc('update_campaign_form_config', {
        p_config_id: metaConfig.id,
        p_campaign_id: campaignExtId,
        p_form_id: formId,
        p_patch: config,
      });
      if (error) throw error;
      const currentConfigs = metaConfig.settings?.campaign_form_configs || {};
      const campaignConfigs = currentConfigs[campaignExtId] || {};
      set({
        metaConfig: {
          ...metaConfig,
          settings: {
            ...(metaConfig.settings || {}),
            campaign_form_configs: {
              ...currentConfigs,
              [campaignExtId]: {
                ...campaignConfigs,
                [formId]: { ...campaignConfigs[formId], ...config },
              },
            },
          },
          updated_at: new Date().toISOString(),
        },
      });
      return true;
    } catch (error: any) {
      console.warn('updateCampaignFormConfig RPC non disponibile, fallback full-blob:', error?.message);
    }

    const currentConfigs = metaConfig.settings?.campaign_form_configs || {};
    const campaignConfigs = currentConfigs[campaignExtId] || {};
    const updatedCampaignConfigs = {
      ...currentConfigs,
      [campaignExtId]: {
        ...campaignConfigs,
        [formId]: { ...campaignConfigs[formId], ...config },
      },
    };
    return get().patchMetaSettings({ campaign_form_configs: updatedCampaignConfigs });
  },

  removeCampaignFormConfig: async (campaignExtId: string, formId: string) => {
    const metaConfig = get().metaConfig;
    if (!metaConfig) return false;

    const currentConfigs = { ...(metaConfig.settings?.campaign_form_configs || {}) };
    if (currentConfigs[campaignExtId]) {
      const campaignConfigs = { ...currentConfigs[campaignExtId] };
      delete campaignConfigs[formId];
      if (Object.keys(campaignConfigs).length === 0) {
        delete currentConfigs[campaignExtId];
      } else {
        currentConfigs[campaignExtId] = campaignConfigs;
      }
    }
    return get().patchMetaSettings({ campaign_form_configs: currentConfigs });
  },

  getMarketingReportSettings: () => {
    const { metaConfig, googleConfig } = get();
    const config = metaConfig || googleConfig;
    return config?.settings?.marketing_report_settings || null;
  },

  diagnoseCampaigns: async () => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) return null;

      const { data, error } = await supabase.functions.invoke('ad-sync-campaigns', {
        body: { organization_id: organizationId, platform: 'meta', mode: 'diagnostic' },
      });

      if (error) {
        console.error('Error diagnosing campaigns:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error diagnosing campaigns:', error);
      return null;
    }
  },

  updateMarketingReportSettings: async (settings: MarketingReportSettings) => {
    const { metaConfig, googleConfig } = get();
    const config = metaConfig || googleConfig;
    if (!config) return false;

    const platform = config.platform as AdPlatform;
    const newSettings = { ...config.settings, marketing_report_settings: settings };
    return get().updateAccountConfig(platform, { settings: newSettings } as any);
  },
}));
